import { useMachine } from '@xstate/react';
import { useHistory } from 'react-router';
import { useCallback, useEffect } from 'react';
import { registrationDeskMachine } from '../state-machine';

/**
* Hook to help wiring the registration desk multi-step form together.
* It initializes the Registration Desk Machine with useMachine and exposes
* its send and snapshot helper functions.
*
* It also hooks up a side effect to handle routing via history pushing.
*/
export default function useRegistrationDeskMultiStepForm() {
  const history = useHistory();
  const [snapshot, send, actorRef] = useMachine(registrationDeskMachine);
  const machineId = snapshot.machine.id;

  const pushPath = useCallback((currentSnapshot: typeof snapshot = snapshot) => {
    const meta = currentSnapshot.getMeta();

    const metaKey = `${machineId}.form.${currentSnapshot.value.form}`;

    if (!(metaKey in meta)) {
      return;
    }

    if (typeof meta[metaKey] !== 'object' || typeof meta[metaKey] == null) {
      return;
    }

    const stateMeta = meta[metaKey] as Record<string, any>;

    if (!('path' in stateMeta) || typeof stateMeta.path !== 'string') {
      return;
    }

    const currentPath = history.location.pathname;

    const { path } = stateMeta;

    if (currentPath === path) {
      return;
    }

    history.push(path);
  }, [history, machineId, snapshot]);

  useEffect(() => {
    const listener = history.listen((_, action) => {
      if (action === 'POP') {
        send({
          type: 'goBack',
        });
      }
    });

    return listener;
  }, [history, send]);

  useEffect(() => {
    /**
     * Ensures that we always reset the path to "/" on page refresh.
     * If we are ever going to implement persistence, this probably would
     * need to be removed in favour other logic (i.e. a form walker of some sorts).
     */
    pushPath();

    const subscription = actorRef.subscribe((newSnapshot) => {
      pushPath(newSnapshot);
    });

    return () => subscription.unsubscribe();
  }, [actorRef]);

  useEffect(() => {
    const subscription = actorRef.subscribe((newSnapshot) => {
      /**
       * Scroll to the main content when resetting the form.
       */
      if (newSnapshot.value.form === 'enteringRegistrationType') {
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 0);

        return;
      }

      /**
       * Scroll to top when the form changes.
       */
      if (newSnapshot.value.form !== snapshot.value.form) {
        setTimeout(() => {
          document.getElementsByClassName('JourneyOverlay__scroll-to-top')[0]?.scrollIntoView({ behavior: 'smooth' });
        }, 0);
      }
    });

    return () => subscription.unsubscribe();
  }, [actorRef, snapshot]);

  return [snapshot, send] as const;
}
